import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, viewChild, inject } from "@angular/core";
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from "@angular/material/dialog";
import { MatStepper, MatStep } from "@angular/material/stepper";
import { Store } from "@ngrx/store";
import { combineLatest, debounceTime, filter, first, mergeMap, Observable, Subject, takeUntil, tap, withLatestFrom } from "rxjs";
import { ButtonType } from "src/app/components/button/button.component";
import { INTERACTION_ID, UUID_REGEX } from "src/app/constants";
import { CustomerActions, FromCustomer, FromRouter, RootState } from "src/app/store";
import { MergeAccountDialogData } from "./merge-account-dialog.interface";
import { AccountDetails } from "@api/accounts";
import { Params } from "@angular/router";
import { User } from "src/app/store/customer/interfaces/user.interface";
import { MergeAccountStatus } from "src/app/store/customer/customer.state";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { FormGroupComponent } from "../../components/form-group/form-group.component";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { BannerComponent } from "../../components/banner/banner.component";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { ButtonComponent } from "../../components/button/button.component";
import { AsyncPipe } from "@angular/common";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-merge-account",
  templateUrl: "./merge-account.component.html",
  styleUrls: ["./merge-account.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    MatStepper,
    CdkScrollable,
    MatDialogContent,
    MatStep,
    FormGroupComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    BannerComponent,
    MatTooltip,
    MatIcon,
    MatDialogActions,
    ButtonComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class MergeAccountComponent implements OnInit, OnDestroy {
  public data = inject<MergeAccountDialogData>(MAT_DIALOG_DATA);
  public readonly dialog = inject<MatDialogRef<MergeAccountComponent>>(MatDialogRef);
  private readonly store = inject<Store<RootState>>(Store);

  private readonly stepper = viewChild<MatStepper>("stepper");

  public form: FormGroup = new FormGroup({
    newCodeAccount: new FormControl<string | undefined>(""),
    newValidAccount: new FormControl<boolean>(false, [Validators.requiredTrue]),
  });

  public readonly buttonTypes: typeof ButtonType = ButtonType;

  public readonly loading$: Observable<boolean> = this.store.select(FromCustomer.selectLoading);
  public readonly queryParams$: Observable<Params> = this.store.select(FromRouter.selectQueryParams);
  public readonly mergeAccount$: Observable<AccountDetails | null> = this.store.select(FromCustomer.selectMergeAccount);
  public readonly mergeAccountError$: Observable<string> = this.store.select(FromCustomer.selectMergeAccountError);
  public readonly mergeAccountStatus$: Observable<MergeAccountStatus> = this.store.select(FromCustomer.selectMergeAccountStatus);

  public readonly accountDetails$: Observable<AccountDetails | undefined> = this.queryParams$.pipe(
    mergeMap(queryParams => this.store.select(FromCustomer.selectAccount(queryParams["accountId"] as string))),
  );

  public readonly user$: Observable<User | undefined> = this.queryParams$.pipe(
    mergeMap(queryParams => this.store.select(FromCustomer.selectUser(queryParams["userId"] as string))),
  );

  public readonly mergeIdentifiersTooltip$: Observable<string> = this.mergeAccount$.pipe(
    mergeMap(mergeAccount => (mergeAccount?.identifiers ?? []).map(i => i.code).join("\n")),
  );

  public readonly identifiersTooltip$: Observable<string> = this.accountDetails$.pipe(
    mergeMap(account => (account?.identifiers ?? []).map(i => i.code).join("\n")),
  );

  private $unsubscribeAll!: Subject<void>;

  public ngOnInit(): void {
    this.$unsubscribeAll = new Subject();

    this.form.controls["newCodeAccount"].valueChanges
      .pipe(
        takeUntil(this.$unsubscribeAll),
        debounceTime(500),
        withLatestFrom(this.queryParams$),
        tap(([newCodeAccountValue, queryParams]) => {
          if (!newCodeAccountValue) {
            this.store.dispatch(CustomerActions.resetMergeAccount());
            return;
          }

          const regexp = new RegExp(UUID_REGEX);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const isUuid = newCodeAccountValue.match(regexp);

          this.store.dispatch(
            CustomerActions.getMergeAccountDetails({
              accountId: queryParams["accountId"] as string,
              params: {
                interactionId: INTERACTION_ID,
                accountId: newCodeAccountValue as string,
                idType: isUuid ? "ID" : "CARDCODE",
              },
            }),
          );
        }),
      )
      .subscribe();

    combineLatest([this.mergeAccount$, this.mergeAccountError$])
      .pipe(
        takeUntil(this.$unsubscribeAll),
        tap(([mergeAccount, mergeAccountError]) => {
          this.form.patchValue({ newValidAccount: !!mergeAccount && !mergeAccountError });
        }),
      )
      .subscribe();

    if (this.data?.code) this.form.patchValue({ newCodeAccount: this.data.code });
  }

  public ngOnDestroy(): void {
    this.store.dispatch(CustomerActions.stopMergeAccountPolling());
    this.store.dispatch(CustomerActions.resetMergeAccount());

    this.$unsubscribeAll.next();
    this.$unsubscribeAll.complete();
  }

  public nextStep(): void {
    const stepper = this.stepper();
    if (stepper) {
      switch (stepper.selectedIndex) {
        case 0:
          if (this.form.valid) stepper.next();
          break;
        default:
          this.merge();
          break;
      }
    }
  }

  public merge(): void {
    combineLatest([this.mergeAccount$, this.queryParams$, this.mergeAccountStatus$])
      .pipe(
        first(),
        filter(([mergeAccount, queryParams, status]) => status === "idle" && !!mergeAccount?.accountId && !!queryParams["accountId"]),
        tap(([mergeAccount, queryParams]) => {
          this.store.dispatch(
            CustomerActions.mergeAccount({
              interactionId: INTERACTION_ID,
              primaryAccountId: queryParams["accountId"] as string,
              secondaryAccountId: mergeAccount?.accountId ?? "",
              primaryIdType: "ID",
              secondaryIdType: "ID",
              callback: () => {
                this.store.dispatch(
                  CustomerActions.getAccountDetails({ interactionId: INTERACTION_ID, accountId: queryParams["accountId"] as string }),
                );
                this.dialog.close();
              },
            }),
          );
        }),
      )
      .subscribe();
  }
}
